import { pointerWithin, type CollisionDetection } from '@dnd-kit/core';
import type { KanbanColumn } from 'types/kanban';

export const PROXIMITY_MARGIN_PX = 24;
const VERTICAL_STRIP_HEIGHT = 1e6;

/** How far (ratio) into a column the drag center must be before switching on touch. */
export const TOUCH_COLUMN_SWITCH_INSET_RATIO = 0.2;

export type ColumnEdgeRole = 'first' | 'last' | 'middle';

export function getColumnEdgeRole(
  columnId: string,
  listIds: string[],
): ColumnEdgeRole {
  if (listIds.length === 0) return 'middle';
  if (columnId === listIds[0]) return 'first';
  if (columnId === listIds[listIds.length - 1]) return 'last';
  return 'middle';
}

/** Extra horizontal reach for the first/last list (covers mobile board padding). */
export function getListHorizontalReach(
  rect: { left: number; right: number; width: number },
  edge: ColumnEdgeRole,
  margin: number,
): { left: number; right: number } {
  let left = rect.left - margin;
  let right = rect.right + margin;
  if (edge === 'first') {
    left = rect.left - rect.width - margin * 2;
  }
  if (edge === 'last') {
    right = rect.right + rect.width + margin * 2;
  }
  return { left, right };
}

export function isDragCenterClearlyInColumn(
  dragCenterX: number,
  columnRect: { left: number; right: number; width: number },
  edge: ColumnEdgeRole = 'middle',
  insetRatio = TOUCH_COLUMN_SWITCH_INSET_RATIO,
): boolean {
  const inset = Math.min(columnRect.width * insetRatio, 48);
  const leftBound =
    edge === 'first'
      ? columnRect.left - columnRect.width - inset
      : columnRect.left + inset;
  const rightBound =
    edge === 'last'
      ? columnRect.right + columnRect.width + inset
      : columnRect.right - inset;
  return dragCenterX >= leftBound && dragCenterX <= rightBound;
}

export function isDragXInListHorizontalReach(
  dragCenterX: number,
  columnRect: { left: number; right: number; width: number },
  edge: ColumnEdgeRole,
  margin: number,
): boolean {
  const { left, right } = getListHorizontalReach(columnRect, edge, margin);
  return dragCenterX >= left && dragCenterX <= right;
}

export function isTouchDragActivator(event: Event | null | undefined): boolean {
  if (event == null) return false;
  if ('touches' in event && (event as TouchEvent).touches.length > 0) {
    return true;
  }
  return (
    'pointerType' in event && (event as PointerEvent).pointerType === 'touch'
  );
}

export function getMemberColor(memberId: string): string {
  let h = 0;
  for (let i = 0; i < memberId.length; i++)
    h = (h << 5) - h + memberId.charCodeAt(i);
  return `hsl(${Math.abs(h) % 360}, 65%, 45%)`;
}

export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Creates a single collision detector: lists use a full-height invisible vertical
 * column (ignores padding/vertical position); cards use expanded rect. Enables
 * switching when slightly outside or under a list.
 */
export function createProximityCollisionDetection(
  listIds: string[],
  isTouchDrag: () => boolean = () => false,
): CollisionDetection {
  const listIdSet = new Set(listIds);
  const firstListId = listIds[0];
  const lastListId = listIds[listIds.length - 1];
  return (args) => {
    const touchDrag = isTouchDrag();
    const activeId = String(args.active.id);
    const activeIsList = listIdSet.has(activeId);
    const { collisionRect, droppableRects, droppableContainers } = args;
    const dragCenterX =
      args.pointerCoordinates?.x ??
      collisionRect.left + collisionRect.width / 2;

    const pointerHits = pointerWithin(args);

    const touchEdgeListHit = (): typeof pointerHits | null => {
      if (!touchDrag || activeIsList || !firstListId) return null;
      for (const listId of [firstListId, lastListId] as string[]) {
        if (!listId) continue;
        const edge = getColumnEdgeRole(listId, listIds);
        if (edge === 'middle') continue;
        const listRect = droppableRects.get(listId);
        if (!listRect) continue;
        if (
          !isDragXInListHorizontalReach(
            dragCenterX,
            listRect,
            edge,
            PROXIMITY_MARGIN_PX,
          )
        ) {
          continue;
        }
        const cardHits = pointerHits.filter(
          (hit) => !listIdSet.has(String(hit.id)),
        );
        const cardHitsInList = cardHits.filter((hit) => {
          const cardRect = droppableRects.get(hit.id);
          if (!cardRect) return false;
          const reach = getListHorizontalReach(
            listRect,
            edge,
            PROXIMITY_MARGIN_PX,
          );
          const cardCenterX = cardRect.left + cardRect.width / 2;
          return cardCenterX >= reach.left && cardCenterX <= reach.right;
        });
        if (cardHitsInList.length > 0) return cardHitsInList;
        return [{ id: listId }];
      }
      return null;
    };

    const edgeHit = touchEdgeListHit();
    if (edgeHit) return edgeHit;

    if (pointerHits.length > 0) {
      // If we're dragging a card, prefer card-item collisions (so the cursor
      // position maps to the exact card index). Only fall back to the full
      // list container when we aren't directly over any card item.
      if (!activeIsList) {
        const cardHits = pointerHits.filter(
          (hit) => !listIdSet.has(String(hit.id)),
        );
        if (cardHits.length > 0) {
          return cardHits;
        }
        const listHits = pointerHits.filter((hit) =>
          listIdSet.has(String(hit.id)),
        );
        if (listHits.length > 0) {
          return listHits;
        }
      }
      return pointerHits;
    }
    const dragCenterY = collisionRect.top + collisionRect.height / 2;
    const margin = PROXIMITY_MARGIN_PX;
    // Touch drags do not keep the finger perfectly centered over the card.
    // Expanding X proximity for cards reduces cases where we fall back to the
    // list container collision and then insert at extremes (first/last).
    const cardMarginX = PROXIMITY_MARGIN_PX * 2;

    type Entry = { id: string; distance: number };
    const entries: Entry[] = [];

    droppableContainers.forEach((container) => {
      const rect = droppableRects.get(container.id);
      if (!rect) return;
      const idStr = String(container.id);
      const isList = listIdSet.has(idStr);

      let left: number;
      let right: number;
      let top: number;
      let bottom: number;
      let centerX: number;
      let centerY: number;

      if (isList) {
        const edge = getColumnEdgeRole(idStr, listIds);
        ({ left, right } = getListHorizontalReach(rect, edge, margin));
        if (touchDrag) {
          // On touch, use the real list bounds so off-screen columns (and
          // scroll-snap jitter) do not register as full-height drop targets.
          top = rect.top - margin;
          bottom = rect.bottom + margin;
          centerY = (top + bottom) / 2;
        } else {
          const halfHeight = VERTICAL_STRIP_HEIGHT / 2;
          top = rect.top + rect.height / 2 - halfHeight;
          bottom = rect.top + rect.height / 2 + halfHeight;
          centerY = rect.top + rect.height / 2;
        }
        centerX = (left + right) / 2;
      } else {
        left = rect.left - cardMarginX;
        right = rect.right + cardMarginX;
        top = rect.top - margin;
        bottom = rect.bottom + margin;
        centerX = (left + right) / 2;
        centerY = (top + bottom) / 2;
      }

      const inProximity =
        dragCenterX >= left &&
        dragCenterX <= right &&
        dragCenterY >= top &&
        dragCenterY <= bottom;
      if (!inProximity) return;
      const distance =
        (dragCenterX - centerX) ** 2 + (dragCenterY - centerY) ** 2;
      entries.push({ id: idStr, distance });
    });

    entries.sort((a, b) => a.distance - b.distance);
    // Prefer card collisions over list-container collisions for card drags.
    const hasCardCollision = entries.some((e) => !listIdSet.has(e.id));
    const finalEntries =
      !activeIsList && hasCardCollision
        ? entries.filter((e) => !listIdSet.has(e.id))
        : entries;
    return finalEntries.map((e) => ({ id: e.id }));
  };
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function generateUuidV4(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
      '',
    );
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
      16,
      20,
    )}-${hex.slice(20)}`;
  }
  // Last-resort fallback for restricted runtimes.
  return `${generateId()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const DEFAULT_LABEL_COLORS = [
  '#61bd4f',
  '#f2d600',
  '#ff9f1a',
  '#eb5a46',
  '#c377e0',
  '#0079bf',
  '#00c2e0',
  '#51e898',
  '#ff78cb',
  '#344563',
  '#b6e6bd',
  '#4ecdc4',
  '#45b7d1',
  '#96ceb4',
  '#ffeaa7',
  '#dfe6e9',
  '#fd79a8',
  '#a29bfe',
  '#6c5ce7',
  '#fab1a0',
  '#ff7675',
  '#74b9ff',
  '#81ecec',
  '#55efc4',
  '#636e72',
  '#2d3436',
];

export const DESCRIPTION_PREVIEW_LENGTH = 300;

/** Scroll the horizontal board so the given list column is centered (mobile snap). */
export function scrollBoardToColumn(
  boardWrapper: HTMLElement | null | undefined,
  columnId: string,
): void {
  if (!boardWrapper) return;
  const columnEl = boardWrapper.querySelector(
    `[data-kanban-column-id="${CSS.escape(columnId)}"]`,
  );
  if (columnEl instanceof HTMLElement) {
    columnEl.scrollIntoView({
      behavior: 'auto',
      inline: 'center',
      block: 'nearest',
    });
  }
}

export function findColumnId(
  columns: KanbanColumn[],
  cardId: string,
): string | null {
  for (const col of columns) {
    if (col.cardIds.includes(cardId)) return col.id;
  }
  return null;
}
